import type {
  CanActivateFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Router } from '@angular/router';
import { inject } from '@angular/core';
import { AuthorizationService } from '../../services/AuthorizationService.service';
import { UserContextService } from '../context/user.context';
import { AuthService } from '../../services/auth.service';
import { PUBLIC_ROUTE_PREFIXES } from './auth.guards';

/**
 * Interfaz para los datos de ruta con permisos
 */
interface RouteDataWithPermission {
  permission?: string;
  permissions?: string[]; // Para múltiples permisos
  requireAll?: boolean; // Si requiere todos los permisos (default: false)
}
export const authorizationGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Promise<boolean | UrlTree> => {
  if (PUBLIC_ROUTE_PREFIXES.some((prefix) => state.url.startsWith(prefix))) {
    return true;
  }

  const router = inject(Router);
  const authorizationService = inject(AuthorizationService);
  const userContext = inject(UserContextService);
  const authService = inject(AuthService);

  try {
    const routeData = route.data as RouteDataWithPermission;
    const { permission, permissions, requireAll = false } = routeData;

    // Validar que se haya especificado al menos un permiso
    if (!permission && (!permissions || permissions.length === 0)) {
      console.error('No se especificó permiso en los datos de la ruta');
      return router.createUrlTree(['/not-authorized']);
    }

    // Asegurar que el usuario esté cargado
    if (!userContext.isUserLoaded()) {
      console.warn('Usuario no cargado en authorizationGuard');
      return router.createUrlTree(['/auth'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // Si los permisos quedaron vacíos (ej: dato desactualizado del backend en el primer
    // ciclo de authGuard), forzar una recarga real desde el servidor antes de evaluar.
    if (authorizationService.obtenerPermisos().length === 0) {
      try {
        await userContext.getUserData(true);
        await authorizationService.cargarPermisos(true);
      } catch (error) {
        console.error('Error al cargar permisos en authorizationGuard:', error);
        return router.createUrlTree(['/not-authorized']);
      }
    }

    // Verificar permisos
    let tieneAcceso = false;

    if (permission) {
      // Un solo permiso
      tieneAcceso = authorizationService.tienePermiso(permission);
    } else if (permissions) {
      // Múltiples permisos
      tieneAcceso = requireAll
        ? authorizationService.tieneTodosLosPermisos(permissions)
        : authorizationService.tieneAlgunPermiso(permissions);
    }

    if (!tieneAcceso) {
      console.warn('Usuario sin permisos suficientes:', { permission, permissions, requireAll });

      // Si el usuario tiene permisos válidos (rol asignado), significa que su perfil
      // cambió y la página actual ya no es accesible para él. Redirigir a su página
      // por defecto en lugar de mostrar "no autorizado".
      const defaultPath = authService.getDefaultPath();
      if (authorizationService.obtenerPermisos().length > 0 && state.url !== defaultPath) {
        return router.createUrlTree([defaultPath]);
      }

      return router.createUrlTree(['/not-authorized']);
    }

    return true;
  } catch (error) {
    console.error('Error inesperado en authorizationGuard:', error);
    return router.createUrlTree(['/not-authorized']);
  }
};
